import "./index.css";
import { clsx } from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "../../i18n";
import { TableContextProvider } from "./context";
import { usePaginationController } from "./hooks/usePaginationController";
import { useSelectionController } from "./hooks/useSelectionController";
import { useSortController } from "./hooks/useSortController";
import { TableBody } from "./TableBody";
import { TableEmptyState } from "./TableEmptyState";
import { TableFooter } from "./TableFooter";
import { TableHeader } from "./TableHeader";
import type { ITableProps, TableContextType } from "./types";
import { getGridTemplateColumns } from "./utils";

export function Table<Item>(properties: ITableProps<Item>) {
  const { t } = useTranslations();
  const {
    items,
    pagination,
    sorting,
    columns,
    selectable,
    loading,
    emptyState,
    ariaLabel,
  } = properties;

  const showFooter = !loading && !emptyState;

  const { sortedItems, sortConfig, onSort } = useSortController({
    items,
    sorting,
    columns,
  });
  const { paginatedItems, paginationConfig, resetPage } =
    usePaginationController({
      items: sortedItems,
      pagination,
    });

  const selection = useSelectionController({
    items: sortedItems,
    itemsOnCurrentPage: paginatedItems,
    selectable,
    pagination: paginationConfig,
  });

  const [previousSortSignature, setPreviousSortSignature] = useState<
    string | null
  >(null);

  useEffect(() => {
    const sortSignature = sortConfig
      ? `${sortConfig.key}-${sortConfig.direction}`
      : null;

    if (pagination === true && sortSignature !== previousSortSignature) {
      if (previousSortSignature !== null) {
        resetPage();
      }
      setPreviousSortSignature(sortSignature);
    }
  }, [pagination, previousSortSignature, resetPage, sortConfig]);

  const gridTemplateColumns = useMemo(() => {
    return getGridTemplateColumns(columns, !!selection);
  }, [columns, selection]);

  const context: TableContextType<Item> = {
    ...properties,
    items: paginatedItems,
    pagination: paginationConfig,
    sorting: sortConfig,
    onSort,
    selection,
    gridTemplateColumns,
  };

  return (
    <TableContextProvider value={context}>
      <div
        className={clsx(
          "nuco-table",
          emptyState && "nuco-table--empty",
          properties.className,
        )}
      >
        <div
          className="nuco-table__inner"
          role="grid"
          aria-label={ariaLabel ?? t("ui.table.dataTableAriaLabel")}
          tabIndex={-1}
        >
          <TableHeader />
          {emptyState ? <TableEmptyState /> : <TableBody<Item> />}
        </div>
        {showFooter && <TableFooter />}
      </div>
    </TableContextProvider>
  );
}
